'use client';

import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import InfiniteGrid from '@/src/components/InfiniteGrid';
import { storesApi } from '../../../../services/stores.service';
import { BasicProductDataResponse } from '@/src/types/catalog';
import { ProductCard } from './ProductCard';
import { useCountryStore } from '@/src/lib/state-stores/currencyStore';
import ProductCardSkeleton from '@/src/components/animation/ProductCardSkeleton';
import Typography from '@/src/components/ui/typography';
import { ProductFilter } from './ProductFilter';
import NotFound from '@/src/components/NotFound';

type Props = {
  branchId: string;
  storeId?: string;
  initialProducts: BasicProductDataResponse[];
  subIndustry?: string;
  category?: string;
  price?: string;
  sortBy?: string;
  direction?: 'asc' | 'desc' | string;
  query?: string;
  subIndustryId?: string;
  storeIdfromBranch?: string;
  haveOffers?: string;
};

export default function InfintyProducts({
  branchId,
  initialProducts,
  category,
  price,
  sortBy,
  direction,
  query,
  subIndustry,
  subIndustryId,
  storeIdfromBranch,
  haveOffers,
}: Props) {
  const searchParams = useSearchParams();
  const { countryId } = useCountryStore();
  const t = useTranslations('product.Filters');
  const tStores = useTranslations('stores');

  const liveSortBy = searchParams.get('sortBy') || searchParams.get('SortBy') || sortBy;
  const liveDirection = searchParams.get('direction') || direction;
  const livePrice = searchParams.get('price') || price;
  const liveHaveOffers = searchParams.get('haveOffers') ?? haveOffers;
  const liveCategory = searchParams.get('CategoryIds') || searchParams.get('category') || category;
  const liveSubIndustryId =
    searchParams.get('IndustryIds') || searchParams.get('subIndustryId') || subIndustryId;
  const liveQuery = searchParams.get('query') || searchParams.get('q') || query;

  const actualSortBy = liveSortBy || (livePrice ? 'price' : undefined);
  const actualDirection =
    liveDirection ||
    (livePrice === 'low' || livePrice === 'true'
      ? 'asc'
      : livePrice === 'high' || livePrice === 'heigh'
        ? 'desc'
        : undefined);

  const isInitialState =
    liveSortBy === sortBy &&
    liveDirection === direction &&
    livePrice === price &&
    liveHaveOffers === haveOffers &&
    liveCategory === category &&
    liveSubIndustryId === subIndustryId &&
    liveQuery === query;

  const fetchProducts = async (pageParam: number | string) => {
    if (liveQuery) {
      try {
        const response = await storesApi.searchProductsForBranch(branchId, {
          page: pageParam.toString(),
          search: liveQuery,
          haveOffers: liveHaveOffers === 'true' ? true : undefined,
        });
        return (response as BasicProductDataResponse[]) || [];
      } catch (error) {
        return [];
      }
    }

    const params = {
      page: pageParam.toString(),
      industryId: liveSubIndustryId?.toString(),
      categoryId: liveCategory,
      IndustryIds: liveSubIndustryId ? [liveSubIndustryId.toString()] : undefined,
      CategoryIds: liveCategory ? [liveCategory] : undefined,
      SortBy: actualSortBy,
      direction: actualDirection as 'asc' | 'desc' | undefined,
      haveOffers: liveHaveOffers === 'true' ? true : undefined,
    };

    try {
      const response = await storesApi.getAllProductsForBranch(branchId, params);
      return (response as BasicProductDataResponse[]) || [];
    } catch (error) {
      return [];
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Typography variant={'h4'} component={'h3'} weight={'bold'}>
          {t('allProducts')}
        </Typography>

        <ProductFilter />
      </div>
      <InfiniteGrid<BasicProductDataResponse>
        queryKey={[
          'products',
          subIndustry,
          liveCategory,
          actualSortBy,
          actualDirection,
          liveQuery,
          liveHaveOffers,
          liveSubIndustryId,
          branchId,
        ]}
        initialData={isInitialState ? initialProducts : undefined}
        fetchFn={fetchProducts}
        pageSize={50}
        gridClassName="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5 lg:grid-cols-5"
        emptyComponent={<NotFound title={tStores('NotProduct')} />}
        renderItem={(item, index) => (
          <ProductCard
            key={item.integrationId || index}
            productDetails={item}
            branchCountryId={countryId}
            branchId={branchId}
          />
        )}
      />
    </div>
  );
}
